/**
 * SurveyBuilder Page
 *
 * Page component that wraps the FormBuilder for creating surveys.
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import FormBuilder from './components/FormBuilder'
import { useAuth } from './auth-context'
import * as surveysApi from './api/surveys'
import './App.css'

export default function SurveyBuilder() {
  const { currentOrg, organizations } = useAuth()
  const navigate = useNavigate()
  const [shareUrl, setShareUrl] = useState('')
  const [surveyTitle, setSurveyTitle] = useState('')
  const [showBanner, setShowBanner] = useState(false)
  const [bannerType, setBannerType] = useState('published') // "published" or "draft"
  const [saving, setSaving] = useState(false)

  // Debug: Log current org on mount and changes
  React.useEffect(() => {
    console.log('[SurveyBuilder] currentOrg:', currentOrg)
    console.log('[SurveyBuilder] organizations:', organizations)
  }, [currentOrg, organizations])

  // Common validation
  const validateForm = (form) => {
    if (!form.title) {
      alert('Please enter a survey title')
      return false
    }

    if (!form.questions || form.questions.length === 0) {
      alert('Please add at least one question')
      return false
    }

    // Warn if no organization is selected but user has organizations
    if (!currentOrg && organizations.length > 0) {
      alert(
        'Warning: No organization selected. The survey will not be linked to any organization. Please select an organization from the dropdown in the navigation bar.'
      )
      return false
    }

    return true
  }

  // Handle Save & Publish
  const handleSave = async (form) => {
    if (!validateForm(form)) return

    setSaving(true)
    try {
      const orgId = currentOrg?.id || (organizations.length > 0 ? organizations[0].id : null)
      console.log('[SurveyBuilder] Creating published survey with org_id:', orgId)

      const response = await surveysApi.createSurvey({
        title: form.title,
        definition: form,
        publish: true,
        organization_id: orgId,
        default_language: form.default_language,
        text_direction: form.text_direction,
        available_languages: form.available_languages,
        allowed_response_types: form.allowed_response_types,
      })

      const url =
        response.short_url ||
        (response.share_url ? `${window.location.origin}${response.share_url}` : null) ||
        `${window.location.origin}/run/${response.id}`

      setShareUrl(url)
      setSurveyTitle(form.title)
      setBannerType('published')
      setShowBanner(true)

      try {
        const message = `📋 Please take a moment to complete this survey:\n\n${form.title}\n\n${url}`
        await navigator.clipboard.writeText(message)
      } catch {
        // Clipboard API may not be available
      }
    } catch (e) {
      console.error('Failed to save survey:', e)
      alert(e.message || 'Failed to save survey')
    } finally {
      setSaving(false)
    }
  }

  // Handle Save as Draft
  const handleSaveDraft = async (form) => {
    if (!validateForm(form)) return

    setSaving(true)
    try {
      const orgId = currentOrg?.id || (organizations.length > 0 ? organizations[0].id : null)
      console.log('[SurveyBuilder] Creating draft survey with org_id:', orgId)

      const response = await surveysApi.createSurvey({
        title: form.title,
        definition: form,
        publish: false, // Save as draft
        organization_id: orgId,
        default_language: form.default_language,
        text_direction: form.text_direction,
        available_languages: form.available_languages,
        allowed_response_types: form.allowed_response_types,
      })

      setSurveyTitle(form.title)
      setBannerType('draft')
      setShowBanner(true)

      // Navigate to survey settings after a short delay
      setTimeout(() => {
        navigate(`/survey/${response.id}/settings`)
      }, 2000)
    } catch (e) {
      console.error('Failed to save draft:', e)
      alert(e.message || 'Failed to save draft')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="surveybuilder-page">
      <header className="app-header">
        <div className="container">
          <h1>Survey Builder</h1>
          <p>Create and design your survey below.</p>
          {currentOrg ? (
            <p style={{ fontSize: '0.9rem', color: '#28a745', marginTop: '0.5rem' }}>
              ✓ Survey will be linked to: <strong>{currentOrg.name}</strong>
            </p>
          ) : organizations.length > 0 ? (
            <p style={{ fontSize: '0.9rem', color: '#dc3545', marginTop: '0.5rem' }}>
              ⚠ No organization selected. Please select one from the navigation bar.
            </p>
          ) : (
            <p style={{ fontSize: '0.9rem', color: '#6c757d', marginTop: '0.5rem' }}>
              Survey will be created without organization link.
            </p>
          )}
        </div>
      </header>
      <main className="survey-main">
        <div className="container" id="survey-wrapper">
          {showBanner && bannerType === 'published' && (
            <div
              className="notice"
              style={{ marginBottom: '0.75rem', background: '#d4edda', borderColor: '#c3e6cb' }}>
              <span>
                ✓ Survey published! Shareable link:{' '}
                <a href={shareUrl} target="_blank" rel="noreferrer">
                  {shareUrl}
                </a>
              </span>
              <div style={{ display: 'flex', gap: '.5rem' }}>
                <button
                  className="btn"
                  onClick={async () => {
                    try {
                      const message = `📋 Please take a moment to complete this survey:\n\n${surveyTitle}\n\n${shareUrl}`
                      await navigator.clipboard.writeText(message)
                    } catch {
                      // Clipboard API may not be available
                    }
                  }}>
                  Copy
                </button>
                <button className="close-btn" onClick={() => setShowBanner(false)}>
                  ✕
                </button>
              </div>
            </div>
          )}
          {showBanner && bannerType === 'draft' && (
            <div
              className="notice"
              style={{ marginBottom: '0.75rem', background: '#fff3cd', borderColor: '#ffc107' }}>
              <span>
                📝 Survey saved as draft! Only you can see it until you publish.
                <br />
                <small>Redirecting to settings where you can publish when ready...</small>
              </span>
              <button className="close-btn" onClick={() => setShowBanner(false)}>
                ✕
              </button>
            </div>
          )}
          <FormBuilder
            onSave={handleSave}
            onSaveDraft={handleSaveDraft}
            saving={saving}
            brandImageUrl={currentOrg?.logo_url || null}
          />
        </div>
      </main>
    </div>
  )
}
